/**
 * Minimal client for Algolia's Composition API, used to return Smart Groups.
 *
 * The Shopify Algolia app only exposes the Search API client
 * (window['algoliasearch/lite']), so compositions are queried directly against
 * the REST endpoints: POST /1/compositions/{compositionID}/run, and
 * GET /1/compositions/{compositionID} to read its sortingStrategy.
 *
 * Params are whitelisted by the API (additionalProperties: false), so anything
 * the Search API accepts but Composition does not - distinct,
 * attributesToRetrieve, sortFacetValuesBy - must be stripped before sending, or
 * the request fails with a 400.
 */
class AlgoliaCompositionClient {
  constructor(appId, apiKey, options = {}) {
    this.appId = appId;
    this.apiKey = apiKey;
    this.userToken = options.userToken || '';
    this.timeout = options.timeout || 5000;

    // Same fallback strategy as Algolia's own clients: the DSN host first, then
    // the three retry hosts in random order to spread load evenly.
    const retryHosts = [`${appId}-1.algolianet.com`, `${appId}-2.algolianet.com`, `${appId}-3.algolianet.com`];

    for (let i = retryHosts.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [retryHosts[i], retryHosts[j]] = [retryHosts[j], retryHosts[i]];
    }

    this.hosts = [`${appId}-dsn.algolia.net`, ...retryHosts];
  }

  /**
   * Credentials go in the query string rather than in headers, and no
   * content-type is set. This keeps the request CORS-safelisted so the browser
   * skips the preflight round trip on every search, which is what Algolia's
   * browser clients do.
   */
  buildUrl(host, path) {
    const credentials = new URLSearchParams({
      'x-algolia-application-id': this.appId,
      'x-algolia-api-key': this.apiKey,
    });

    return `https://${host}${path}?${credentials.toString()}`;
  }

  compositionPath(compositionID) {
    if (!compositionID) {
      throw new Error('AlgoliaCompositionClient: a composition ID is required');
    }

    return `/1/compositions/${encodeURIComponent(compositionID)}`;
  }

  async run(compositionID, params) {
    const path = `${this.compositionPath(compositionID)}/run`;

    const body = JSON.stringify({
      params: {
        ...params,
        ...(this.userToken ? { userToken: this.userToken } : {}),
      },
    });

    return this.requestWithRetries((host) =>
      this.request(host, path, { method: 'POST', body })
    );
  }

  /**
   * Read a composition's settings, including its sortingStrategy. The
   * search-only key is allowed to read them, so this needs no extra ACL.
   */
  async getComposition(compositionID) {
    const path = this.compositionPath(compositionID);

    return this.requestWithRetries((host) =>
      this.request(host, path, { method: 'GET' })
    );
  }

  async requestWithRetries(send) {
    let lastError;

    for (const host of this.hosts) {
      try {
        return await send(host);
      } catch (error) {
        lastError = error;

        // A rejected key, a missing composition or an unsupported param will be
        // rejected the same way by every host, so stop rather than retrying.
        if (error.retryable === false) {
          throw error;
        }
      }
    }

    throw lastError;
  }

  async request(host, path, init) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(this.buildUrl(host, path), {
        ...init,
        signal: controller.signal,
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(payload.message || `Composition request failed with status ${response.status}`);
        error.status = response.status;
        error.retryable = response.status >= 500;
        throw error;
      }

      return payload;
    } finally {
      clearTimeout(timer);
    }
  }
}

window.AlgoliaCompositionClient = AlgoliaCompositionClient;
